import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { Dispatch, ReactNode, SetStateAction } from "react";

import { psApi } from "api";

import type { SelectOption, StringSelectOption } from "models/common.model";

import { useAuth } from "./AuthContext";

// Reference data fetched once per session: payment systems and the promocode
// dictionaries. Nothing here changes while a page is being used.
type DictionariesValue = {
  psOptions: SelectOption[];
  accrualTypes: StringSelectOption[];
  codeTypes: StringSelectOption[];
  setPsOptions: Dispatch<SetStateAction<SelectOption[]>>;
  setAccrualTypes: Dispatch<SetStateAction<StringSelectOption[]>>;
  setCodeTypes: Dispatch<SetStateAction<StringSelectOption[]>>;
  refreshPsOptions: () => void;
};

const DictionariesContext = createContext<DictionariesValue | null>(null);

DictionariesContext.displayName = "DictionariesContext";

function DictionariesProvider({ children }: { children: ReactNode }) {
  const { isAuthorized } = useAuth();
  const [psOptions, setPsOptions] = useState<SelectOption[]>([]);
  const [accrualTypes, setAccrualTypes] = useState<StringSelectOption[]>([]);
  const [codeTypes, setCodeTypes] = useState<StringSelectOption[]>([]);

  const refreshPsOptions = useCallback(() => {
    psApi.getPayments().then((info) => {
      if (info) setPsOptions(info);
    });
  }, []);

  useEffect(() => {
    if (!isAuthorized) return;

    refreshPsOptions();
  }, [isAuthorized, refreshPsOptions]);

  const value = useMemo(
    () => ({
      psOptions: psOptions,
      accrualTypes: accrualTypes,
      codeTypes: codeTypes,
      setPsOptions: setPsOptions,
      setAccrualTypes: setAccrualTypes,
      setCodeTypes: setCodeTypes,
      refreshPsOptions: refreshPsOptions,
    }),
    [psOptions, accrualTypes, codeTypes, refreshPsOptions]
  );

  return <DictionariesContext.Provider value={value}>{children}</DictionariesContext.Provider>;
}

function useDictionaries(): DictionariesValue {
  const context = useContext(DictionariesContext);

  if (!context) {
    throw new Error("useDictionaries must be used inside AppProvider.");
  }

  return context;
}

export { DictionariesProvider, useDictionaries };
